import React from "react";
import { BodyText, Card, CardContent, HeadingText } from "../../../shared/ui";
import { DonutChart } from "./DonutChart";
import { DonutChartSegment } from "../types";

interface TransactionMetricCardProps {
  label: string;
  value: string | number;
  subtext?: React.ReactNode;
  donutSegments?: DonutChartSegment[];
  donutSize?: number;
  legend?: Array<{ label: string; color: string }>;
  className?: string;
}

export const TransactionMetricCard: React.FC<TransactionMetricCardProps> = ({
  label,
  value,
  subtext,
  donutSegments,
  donutSize = 64,
  legend,
  className = "",
}) => {
  const isLongValue = typeof value === "string" && value.length > 13;

  return (
    <Card
      className={`min-w-0 w-full rounded-[16px] border border-[#E1E6FF] bg-white shadow-none transition-shadow hover:shadow-xs ${className}`}
    >
      <CardContent className="flex h-full min-h-[136px] sm:min-h-[148px] flex-col justify-between p-4 sm:p-5 md:p-6">
        <div className="flex items-start justify-between gap-2 sm:gap-3">
          <div className="min-w-0 flex-1">
            <HeadingText className="aku-heading-h5-medium truncate text-[14px] sm:text-[15px] xl:text-[16px] text-[#161827]">
              {label}
            </HeadingText>
            <p
              className={`mt-2 font-medium tracking-[-0.03em] text-[#161827] ${
                isLongValue
                  ? "text-[18px] sm:text-[20px] md:text-[22px] lg:text-[19px] xl:text-[24px] 2xl:text-[26px]"
                  : "text-[22px] sm:text-[24px] lg:text-[22px] xl:text-[26px] 2xl:text-[28px]"
              } break-words`}
              title={typeof value === "string" ? value : undefined}
            >
              {value}
            </p>
          </div>

          {donutSegments && donutSegments.length > 0 && (
            <div className="flex shrink-0 items-center justify-center pl-1 sm:pl-2">
              <DonutChart
                segments={donutSegments}
                size={donutSize}
                strokeWidth={11}
                className="h-12 w-12 sm:h-14 sm:w-14 xl:h-16 xl:w-16"
              />
            </div>
          )}
        </div>

        {(subtext || (legend && legend.length > 0)) && (
          <div className="mt-3 sm:mt-4 pt-1">
            {legend && legend.length > 0 ? (
              <div className="flex flex-wrap items-center gap-x-3 sm:gap-x-4 gap-y-1.5 aku-label-regular text-[12px] sm:text-[13px] text-[#414562]">
                {legend.map((item, idx) => (
                  <span key={idx} className="flex shrink-0 items-center gap-1.5">
                    <span
                      className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                      style={{ backgroundColor: item.color }}
                      aria-hidden="true"
                    />
                    <span className="whitespace-nowrap">{item.label}</span>
                  </span>
                ))}
              </div>
            ) : (
              <BodyText
                className="aku-label-regular text-[12px] sm:text-[13px] text-[#4C5173] break-words"
                title={typeof subtext === "string" ? subtext : undefined}
              >
                {subtext}
              </BodyText>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
